
import { computed, defineComponent, inject, PropType, ref } from 'vue';
import { debounce } from 'lodash';
import { Option } from '@/types';
import { NumberWithMetric } from '@/types/shared';
import { debounceTime, InjectionKeys } from '@/constants';
import FilterableSelect from '@/components/form-controls/base/FilterableSelect.vue';
import TextInputStyleProvider from '@/components/style-providers/TextInputStyleProvider.vue';
import Label from '@/components/form-controls/base/Label.vue';

export default defineComponent({
  name: 'NumberWithMetricInput',
  emits: ['update:modelValue', 'input', 'blur'],
  components: {
    FilterableSelect,
    TextInputStyleProvider,
    Label
  },
  props: {
    modelValue: {
      type: Object as PropType<NumberWithMetric>,
      required: true
    },
    metricList: {
      type: Array as PropType<Array<[string | number, string | number]>>,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    isDebounced: {
      type: Boolean,
      default: false
    },
    step: {
      type: Number,
      default: 0.01
    },
    required: {
      type: Boolean
    }
  },

  setup(props, { emit }) {
    const setDirty = inject(InjectionKeys.SetFormDirty);
    const emitValue = (newValue: NumberWithMetric) => emit('update:modelValue', newValue);
    const emitValueDebounced = debounce(newValue => emit('update:modelValue', newValue), debounceTime, {
      trailing: true
    });
    const highlight = ref(false);
    const state = computed({
      get: () => props.modelValue,
      set: newValue => {
        if (newValue.number.toString().includes('-') || Number.isNaN(newValue.number)) {
          newValue.number = 0;
        }

        if (props.isDebounced) {
          emitValueDebounced(newValue);
        } else {
          emitValue(newValue);
        }

        setDirty?.();
        highlight.value = false;
      }
    });

    const checkEmptyInput = (event: Event) => {
      highlight.value = false;
      const input = event.target as HTMLInputElement;
      const checkVal = input.checkValidity();
      if (!checkVal) {
        highlight.value = true;
      }
    };

    const setHighlight = (condition: boolean) => {
      highlight.value = condition;
    };

    const handleInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const newValue = { ...props.modelValue, number: parseFloat(input.value) };
      state.value = newValue; // Triggers the computed setter.
    };

    const options = computed(() =>
      props.metricList.map<Option<string | number>>(([, value]) => ({
        label: value,
        value
      }))
    );

    return {
      setDirty,
      options,
      state,
      handleInput,
      checkEmptyInput,
      highlight,
      setHighlight
    };
  }
});
